import type { TablePagination } from '@sparklayer-io/spark-dash-ui/components/ui/table/types';
import { Badge, Button, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import type { Company } from '../types/company';

export type CompaniesTableProps = {
  companies: Company[];
  pagination?: TablePagination;
};

export function CompaniesTable({ companies, pagination }: CompaniesTableProps): JSX.Element {
  return (
    <Table
      caption={t('features:companies.pages.companies_list_page.table.caption')}
      headings={[
        t('features:companies.pages.companies_list_page.table.headings.name'),
        t('features:companies.pages.companies_list_page.table.headings.primary_customer'),
        t('features:companies.pages.companies_list_page.table.headings.customer_group'),
        '',
      ]}
      pagination={pagination}
    >
      {companies.map((company, index) => (
        <CompaniesTableRow key={company.id} company={company} index={index} />
      ))}
    </Table>
  );
}

function CompaniesTableRow({ company, index }: { company: Company; index: number }) {
  const { navigationHandler } = useHandlers();

  const primaryCustomerName = company.primary_customer
    ? [company.primary_customer.first_name, company.primary_customer.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || company.primary_customer.email
    : undefined;

  return (
    <Table.Row index={index} id={company.id}>
      <Table.Cell>
        <Text>{company.name}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text variant="secondary">{primaryCustomerName ?? '—'}</Text>
      </Table.Cell>
      <Table.Cell>
        {company.customer_group ? <Badge variant="green">{company.customer_group}</Badge> : null}
      </Table.Cell>
      <Table.Cell>
        <Button
          variant="secondary"
          onClick={() => navigationHandler.navigate(`/customers/companies/${company.id}/sections`)}
        >
          {t('features:companies.pages.companies_list_page.table.buttons.sections')}
        </Button>
      </Table.Cell>
      <Table.Cell sticky>
        <Button
          variant="secondary"
          onClick={() => navigationHandler.navigate(`/customers/companies/edit/${company.id}`)}
        >
          {t('buttons:edit')}
        </Button>
      </Table.Cell>
    </Table.Row>
  );
}
